import { useCallback } from 'react';

import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../../../utils/back-errors.util';
import type { BackError } from '../../../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { addressForm2ApiMapper } from '../../../../Address/mappers/address.form2api.mapper';
import type { AddressForm_Address } from '../../../../Address/fragments/address.fragment';
import type { AddressFormTab } from '../../../../Address/enums/tab.enum';

import { useCreateThirdPartyAddressMutation } from './useCreateThirdPartyAddressMutation.hook';

interface Params {
  readonly handleCreateSuccess: () => void;
  readonly thirdPartyId: Id;
  readonly useDirectMutation: boolean;
}

interface FormikArrayActions {
  readonly createInForm: (value: Readonly<AddressForm_Address>) => void;
}

export const useCreateThirdPartyAddressSubmit = ({
  handleCreateSuccess,
  thirdPartyId,
  useDirectMutation,
}: Readonly<Params>) => {
  const { mutate: createMutation } = useCreateThirdPartyAddressMutation();

  const handleSubmitFactory = useCallback(
    ({ createInForm }: Readonly<FormikArrayActions>) =>
      ({
        values,
        memos,
        formikHelpers: { setSubmitting },
        setWizardError,
      }: Readonly<
        WizardSubmitHandlerParams<AddressForm_Address, AddressFormTab>
      >) => {
        setWizardError(undefined);

        const onError = (error: unknown) => {
          setWizardError({ messages: userFriendlyErrors(error as BackError) });
          setSubmitting(false);
        };

        const onSuccess = () => {
          createInForm(values);

          handleCreateSuccess();
          setSubmitting(false);
        };

        if (useDirectMutation) {
          createMutation(
            {
              thirdPartyId,
              address: addressForm2ApiMapper(values),
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [createMutation, handleCreateSuccess, thirdPartyId, useDirectMutation],
  );

  return { handleSubmitFactory };
};
